import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ChevronDown, Database, Package, Tag, CheckCircle2,
  Search, Users, Mail, RefreshCw, Activity, Server, TrendingUp,
  ShieldCheck, AlertTriangle, Boxes, Sparkles, Link2, HardDrive,
  Layers, Zap, Loader2, History, Trash2,
} from "lucide-react";
import UserMenu from "../components/UserMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("pr_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * SAP Workspace — ERP-style landing page that consolidates every SAP-adjacent
 * capability in the app under a single top-nav with 4 dropdown menus.
 *
 * Landing view is a health dashboard: SAP broker connectivity + last-sync
 * counters + quick-stat tiles that click through to the relevant page.
 * The menus are the primary navigation surface — modelled loosely on SAP
 * Fiori Launchpad's category-with-flyout pattern.
 */

// ---- Menu structure — kept as data so it's trivially re-orderable ----
const MENUS = [
  {
    key: "procurement", label: "Procurement", icon: Package,
    items: [
      { label: "MRP Purchase Proposals",  to: "/mrp-proposals",         icon: Boxes,      desc: "Buyer worklist · 7,195 actionable", featured: true },
      { label: "PR Pipeline",             to: "/pipeline",              icon: TrendingUp, desc: "Live PR flow · L1 → L2 → PO" },
      { label: "Create PR",               to: "/create",                icon: Sparkles,   desc: "New PR (single or from MRP proposal)" },
      { label: "My PRs",                  to: "/my-prs",                icon: CheckCircle2, desc: "Your drafts + submitted PRs" },
      { label: "Auto-Approve PR Report",  to: "/reports/auto-approve-prs", icon: History,  desc: "Auto-approved PRs · who created · past & recent changes" },
    ],
  },
  {
    key: "pricing", label: "Pricing & Rates", icon: Tag,
    items: [
      { label: "List Price Management",   to: "/admin/list-prices",           icon: Tag,          desc: "Standing SAP prices · SOAP write-back", featured: true },
      { label: "List Price Approvals",    to: "/admin/list-price-approvals",  icon: CheckCircle2, desc: "L1/L2 approval inbox" },
      { label: "My Approvals History",    to: "/admin/list-price-history",    icon: History,      desc: "What you've approved · SAP-written only" },
      { label: "Price Explorer",          to: "/price-explorer",              icon: Search,       desc: "Cross-supplier price history" },
    ],
  },
  {
    key: "masterdata", label: "Master Data", icon: Database,
    items: [
      { label: "SAP Data Mirror",         to: "/admin/sap-data",         icon: HardDrive,    desc: "All mirrored SAP collections + sync triggers", featured: true },
      { label: "Data Cleanup",            to: "/admin/sap-data?tab=cleanup", icon: Trash2,   desc: "Purge phantom / zero-amount stub rows · admin only" },
      { label: "Buyer Assignments",       to: "/admin/buyer-assignments",icon: Users,        desc: "Map material types to owning buyers" },
    ],
  },
  {
    key: "integration", label: "Integration & Audit", icon: Link2,
    items: [
      { label: "SAP Health · Call Metrics", to: "/admin/sap-health",     icon: Link2,        desc: "p50 / p95 / error-rate per SAP endpoint · last 24h" },
      { label: "Email Dispatch Log",      to: "/admin/email-log",        icon: Mail,         desc: "MS Graph telemetry · SAP-triggered mails" },
      { label: "Admin Console",           to: "/admin",                  icon: ShieldCheck,  desc: "Users, policies, SAP maintenance" },
    ],
  },
];

// Cache-friendly relative-age formatter — used in a dozen places on this page.
const fmtRelativeAge = (isoStr) => {
  if (!isoStr) return "never";
  const d = new Date(isoStr);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function SapHub() {
  const nav = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const menuRef = useRef(null);

  // Close the flyout when clicking outside — standard ERP pattern.
  useEffect(() => {
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(l => l || true);
    try {
      const [mrpR, tierR] = await Promise.all([
        axios.get(`${API}/mrp-proposals?limit=1`, { headers: authHeaders() }).catch(() => null),
        axios.get(`${API}/admin/tierless-log?limit=1&since_days=14`, { headers: authHeaders() }).catch(() => null),
      ]);
      // Broker health is admin-scoped; catch 403 gracefully.
      let hr = null;
      try {
        hr = (await axios.get(`${API}/admin/sap/health`, { headers: authHeaders() })).data;
      } catch (_) { /* non-admin — skip */ }

      setHealth(hr);
      setStats({
        mrp_total: mrpR?.data?.total ?? null,
        mrp_last_synced: mrpR?.data?.last_synced ?? null,
        mrp_sync_ok: mrpR?.data?.sync_ok ?? null,
        tier_counters: tierR?.data?.counters ?? {},
        tier_eligible: tierR?.data?.eligible_count ?? 0,
        shadow_mode: tierR?.data?.shadow_mode ?? true,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/40 to-slate-50">
      {/* Top navigation — ERP-style category strip */}
      <header className="bg-cyan-700 text-white shadow-lg sticky top-0 z-40">
        {/* Row 1: branding + user actions */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-12 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button onClick={() => nav("/home")}
                    className="text-cyan-100 hover:text-white transition-colors flex items-center gap-1 text-fiori-caption shrink-0"
                    data-testid="sap-hub-back-btn"
                    aria-label="Back to Home">
              <ArrowLeft size={14}/>
              <span className="hidden sm:inline">Home</span>
            </button>
            <div className="h-5 w-px bg-cyan-500/60 shrink-0"/>
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-sm bg-white/10 flex items-center justify-center shrink-0">
                <Layers size={15}/>
              </div>
              <div className="min-w-0">
                <div className="text-fiori-h2 text-white leading-tight truncate" data-testid="sap-hub-brand">SAP Workspace</div>
                <div className="text-fiori-micro text-cyan-100/90 truncate hidden sm:block">Business ByDesign · Integration Console</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 text-cyan-100 shrink-0">
            <button
              onClick={() => { setRefreshing(true); loadDashboard(); }}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 hover:bg-white/10 rounded-sm transition-colors text-fiori-caption"
              data-testid="sap-hub-refresh"
              aria-label="Refresh"
            >
              {refreshing ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <div className="h-5 w-px bg-cyan-500/60 mx-0.5 sm:mx-1 hidden sm:block"/>
            <UserMenu tone="dark" />
          </div>
        </div>

        {/* Row 2: menu strip — horizontally scrollable on mobile so all
            four categories stay reachable without wrapping. On desktop
            the strip is `overflow-visible` so vertical dropdowns aren't
            clipped by the scroll container (CSS silently promotes
            overflow-y to auto whenever overflow-x is set — the exact
            reason the Sep-26 mobile rework broke dropdowns in prod). */}
        <nav ref={menuRef} className="border-t border-cyan-600/70 bg-cyan-800/40 backdrop-blur">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 h-9 flex items-center gap-1 overflow-x-auto sm:overflow-visible scrollbar-hide">
            {MENUS.map((menu) => {
              const Icon = menu.icon;
              const isOpen = openMenu === menu.key;
              return (
                <div key={menu.key} className="relative shrink-0">
                  <button
                    onClick={() => setOpenMenu(isOpen ? null : menu.key)}
                    className={`flex items-center gap-1.5 px-2.5 sm:px-3 h-9 text-fiori-h2 whitespace-nowrap transition-colors border-b-2 ${
                      isOpen
                        ? "text-white border-white bg-white/10"
                        : "text-cyan-100 border-transparent hover:text-white hover:bg-white/10"
                    }`}
                    data-testid={`sap-menu-${menu.key}`}
                  >
                    <Icon size={13}/>
                    {menu.label}
                    <ChevronDown size={11} className={`transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                  </button>
                  {isOpen && (
                    <div className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:left-0 top-[84px] sm:top-full sm:mt-0 sm:w-80 bg-white text-slate-900 rounded-sm sm:rounded-b-sm shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-top-1 max-h-[70vh] overflow-y-auto"
                         data-testid={`sap-menu-panel-${menu.key}`}>
                      {menu.items.map((item) => {
                        const ItemIcon = item.icon;
                        return (
                          <button
                            key={item.to}
                            onClick={() => { setOpenMenu(null); nav(item.to); }}
                            className={`w-full text-left px-3 py-2.5 flex items-start gap-2 hover:bg-cyan-50 transition-colors border-b border-slate-100 last:border-0 ${
                              item.featured ? "bg-cyan-50/40" : ""
                            }`}
                            data-testid={`sap-menu-item-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                          >
                            <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${
                              item.featured
                                ? "bg-cyan-700 text-white"
                                : "bg-slate-100 text-slate-700"
                            }`}>
                              <ItemIcon size={14}/>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-fiori-body font-semibold text-slate-900 flex items-center gap-1.5">
                                {item.label}
                                {item.featured && <span className="text-fiori-micro bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded">Featured</span>}
                              </div>
                              <div className="text-fiori-caption mt-0.5">{item.desc}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Section 1: SAP broker connectivity — admin only */}
        {health && (
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-fiori-h2 text-slate-900 flex items-center gap-1.5">
                <Server size={15} className="text-cyan-700"/>
                SAP Broker Connectivity
              </h2>
              <span className="text-fiori-micro text-slate-500">
                {health.broker_ok ? "🟢 Live" : "🔴 Broker offline"}
              </span>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="grid grid-cols-2 md:grid-cols-6 divide-x divide-slate-100">
                {(health.credentials ? Object.entries(health.credentials) : []).slice(0, 6).map(([user, meta]) => (
                  <div key={user} className="p-3 text-center">
                    <div className={`text-fiori-micro mb-0.5 ${
                      meta.ok ? "text-emerald-700" : "text-red-700"
                    }`}>
                      {meta.ok ? "🟢 OK" : "❌ " + (meta.status || "fail")}
                    </div>
                    <div className="font-mono text-fiori-body font-semibold text-slate-900 truncate">{user}</div>
                    <div className="text-fiori-caption">{meta.category}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Section 2: Key metrics tiles */}
        <section>
          <h2 className="text-fiori-h2 text-slate-900 mb-2 flex items-center gap-1.5">
            <Activity size={15} className="text-cyan-700"/>
            Snapshot
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* MRP tile */}
            <button
              onClick={() => nav("/mrp-proposals")}
              className="group bg-white border border-slate-200 hover:border-cyan-500 hover:shadow-md rounded-sm p-4 text-left transition-all"
              data-testid="sap-tile-mrp"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="w-9 h-9 rounded-sm bg-cyan-100 text-cyan-800 flex items-center justify-center group-hover:bg-cyan-700 group-hover:text-white transition-colors">
                  <Boxes size={18}/>
                </div>
                <span className={`text-fiori-micro ${
                  stats?.mrp_sync_ok ? "text-emerald-700" : "text-amber-700"
                }`}>
                  {stats?.mrp_sync_ok === false ? "⚠ Sync issue" : stats?.mrp_sync_ok ? "🟢 Fresh" : ""}
                </span>
              </div>
              <div className="text-fiori-metric text-slate-900" data-testid="sap-tile-mrp-count">
                {loading ? "…" : (stats?.mrp_total ?? 0).toLocaleString()}
              </div>
              <div className="text-fiori-h2 text-slate-700 mt-1">MRP proposals actionable</div>
              <div className="text-fiori-caption mt-1">
                synced {fmtRelativeAge(stats?.mrp_last_synced)}
              </div>
            </button>

            {/* Tierless tile */}
            <button
              onClick={() => nav("/admin/tierless-log").catch?.(()=>{})}
              className="group bg-white border border-slate-200 hover:border-emerald-500 hover:shadow-md rounded-sm p-4 text-left transition-all"
              data-testid="sap-tile-tierless"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="w-9 h-9 rounded-sm bg-emerald-100 text-emerald-800 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <Zap size={18}/>
                </div>
                {stats?.shadow_mode && (
                  <span className="text-fiori-micro text-amber-700">👁 Shadow</span>
                )}
              </div>
              <div className="text-fiori-metric text-slate-900">
                {loading ? "…" : (stats?.tier_eligible ?? 0)}
                <span className="text-fiori-body text-slate-500 ml-1">
                  / {loading ? "…" : Object.values(stats?.tier_counters || {}).reduce((a,b)=>a+b,0)}
                </span>
              </div>
              <div className="text-fiori-h2 text-slate-700 mt-1">Tierless-eligible (14d)</div>
              <div className="text-fiori-caption mt-1 flex items-center gap-1.5 flex-wrap">
                {Object.entries(stats?.tier_counters || {}).map(([k,v]) => (
                  <span key={k}>{k.replace("_","+")}:{v}</span>
                ))}
              </div>
            </button>

            {/* List Prices tile */}
            <button
              onClick={() => nav("/admin/list-price-approvals")}
              className="group bg-white border border-slate-200 hover:border-blue-500 hover:shadow-md rounded-sm p-4 text-left transition-all"
              data-testid="sap-tile-list-prices"
            >
              <div className="w-9 h-9 rounded-sm bg-blue-100 text-blue-800 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors mb-2">
                <Tag size={18}/>
              </div>
              <div className="text-fiori-metric text-slate-900">Live</div>
              <div className="text-fiori-h2 text-slate-700 mt-1">SAP List Prices · L1/L2 approvals</div>
              <div className="text-fiori-caption mt-1">SOAP write-back active</div>
            </button>

            {/* SAP Data Mirror tile */}
            <button
              onClick={() => nav("/admin/sap-data")}
              className="group bg-white border border-slate-200 hover:border-slate-500 hover:shadow-md rounded-sm p-4 text-left transition-all"
              data-testid="sap-tile-mirror"
            >
              <div className="w-9 h-9 rounded-sm bg-slate-100 text-slate-800 flex items-center justify-center group-hover:bg-slate-800 group-hover:text-white transition-colors mb-2">
                <HardDrive size={18}/>
              </div>
              <div className="text-fiori-metric text-slate-900">Mirror</div>
              <div className="text-fiori-h2 text-slate-700 mt-1">Items · Suppliers · BOMs · POs</div>
              <div className="text-fiori-caption mt-1">Manual + scheduled OData syncs</div>
            </button>
          </div>
        </section>

        {/* Section 3: Feature spotlight — split-convert + tierless callout */}
        <section className="bg-gradient-to-r from-cyan-700 via-cyan-800 to-teal-800 text-white rounded-sm overflow-hidden shadow-md">
          <div className="p-5 sm:p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-sm bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Sparkles size={22}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-fiori-micro opacity-80 mb-1">
                Feb 2026 · Newly Live
              </div>
              <h3 className="text-fiori-h1 mb-1">Tierless Approval + Split-Convert</h3>
              <p className="text-fiori-body opacity-90 mb-3">
                MRP-sourced, list-price-validated PRs under ₹5L now qualify for zero-touch approval
                (shadow-mode until Mar 6). Split any proposal across up to N vendors — server
                validates the ±2% tolerance, 80% concentration cap, and tier per allocation live.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => nav("/mrp-proposals")}
                  className="text-fiori-h2 px-3 py-1.5 bg-white text-cyan-800 rounded-sm hover:bg-cyan-50 transition-colors"
                  data-testid="sap-hub-cta-mrp"
                >
                  Open MRP Inbox →
                </button>
                <button
                  onClick={() => nav("/admin/buyer-assignments")}
                  className="text-fiori-h2 px-3 py-1.5 bg-white/15 hover:bg-white/25 backdrop-blur rounded-sm transition-colors"
                  data-testid="sap-hub-cta-assignments"
                >
                  Set Buyer Assignments
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer note */}
        <div className="text-fiori-caption text-center pt-4">
          SAP Business ByDesign · tenant <span className="font-mono">my431827</span> · endpoint health monitored every load
        </div>
      </main>
    </div>
  );
}
